import React, { memo, useEffect, useState, useCallback, ChangeEvent, KeyboardEvent } from 'react';
import { Input, IconButton } from '@one-thd/sui-atomic-components';
import { Search, Close } from '@one-thd/sui-icons';

export interface SearchInputProps {
  value?: string;
  onSubmit: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
}

const SearchInputComponent: React.FC<SearchInputProps> = memo(
  ({ value = '', onSubmit, placeholder = 'Search', ariaLabel = 'Search' }: SearchInputProps) => {
    useEffect(() => {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search-input.ready');
    }, []);

    const [draft, setDraft] = useState(value);

    useEffect(() => {
      setDraft(value);
    }, [value]);

    const submit = useCallback(() => {
      onSubmit(draft.trim());
    }, [draft, onSubmit]);

    const handleClear = useCallback(() => {
      setDraft('');
      // Always re-submits the empty string — the request should always reflect what's in the
      // field, so clearing drops it back to the default (no-search) state and re-fetches, even
      // if nothing had been explicitly submitted yet.
      onSubmit('');
    }, [onSubmit]);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const nextDraft = event.target.value;
        setDraft(nextDraft);
        // Emptying the field by any means (backspace/delete, not just Clear) re-submits
        // immediately, same as handleClear above, rather than leaving a stale request applied.
        if (nextDraft === '') {
          onSubmit('');
        }
      },
      [onSubmit]
    );

    return (
      // Input has no size/className override for its internal root height/padding, so pin it to
      // the same sui-h-9 as the other toolbar controls (DateRangeFilter, FilterTriggerButton,
      // ColumnCustomizationMenu) via a descendant override — otherwise the default sui-py-3 input
      // padding makes this control taller than the rest.
      <div className="[&_.sui-input-base-root]:sui-h-9 [&_.sui-input-base-input]:sui-py-2">
        <Input
          value={draft}
          placeholder={placeholder}
          fullWidth
          onChange={handleChange}
          inputProps={{
            'aria-label': ariaLabel,
            onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') {
                submit();
              }
            }
          }}
          endAdornment={
            <div className="sui-flex sui-items-center sui-gap-1">
              {draft.length > 0 && <IconButton aria-label="Clear search" icon={Close} onClick={handleClear} />}
              <IconButton aria-label="Submit search" icon={Search} color="brand" onClick={submit} />
            </div>
          }
        />
      </div>
    );
  }
);

SearchInputComponent.displayName = 'SearchInput';

const SearchInput = SearchInputComponent;

export { SearchInput };
